import { ApolloClient, ApolloLink, split, InMemoryCache } from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { env } from '../utils/env';
import { createUploadLink } from 'apollo-upload-client';
import { defaultApolloCache } from './defaultCacheData';
import omitDeep from 'omit-deep-lodash';
import i18next from 'i18next';
import { networkStatusLink } from '@ts-private/apollo-tools-ts';

let access_token;

// Create a WebSocket link:
let wsLink;
let activeSocket;

export const closeSocket = reason => {
  if (activeSocket && activeSocket.readyState === WebSocket.OPEN) {
    activeSocket.close(1000, reason);
  }
};

const getWsLink = () => {
  let currentAccessToken;
  if (!wsLink) {
    const wsClient = createClient({
      url: `${env.REACT_APP_GQL_WS_PROTOCOL}://${env.REACT_APP_GQL}/graphql`,
      keepAlive: 10000, // ping server every 10 seconds,
      connectionParams: async () => {
        currentAccessToken = access_token;
        return {
          authorization: access_token ? `Bearer ${access_token}` : '',
          'Accept-Language': i18next?.language
        };
      },
      on: {
        connected: socket => (activeSocket = socket),
        ping: async received => {
          if (!received) {
            if (activeSocket.readyState === WebSocket.OPEN && currentAccessToken !== access_token) {
              setTimeout(() => {
                closeSocket('Access token silent renew');
              }, 1000); // wait 1 second for the pong and then close the connection
            }
          }
        },
        closed: event => {
          console.log(`GraphQL WebSocket closed!: ${event.code} Reason: ${event.reason}`);
        }
      }
    });
    wsLink = new GraphQLWsLink(wsClient);
    wsLink.setOnError(
      onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors)
          graphQLErrors.map(({ message, locations, path }) =>
            console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
          );
        if (networkError) console.log(`[Network error]: ${networkError}`);
      })
    );
  }
  return wsLink;
};

const httpLink = createUploadLink({
  uri: `${env.REACT_APP_GQL_HTTP_PROTOCOL}://${env.REACT_APP_GQL}/graphql`,
  headers: { 'Apollo-Require-Preflight': 'true' },
  onError: onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors)
      graphQLErrors.map(({ message, locations, path }) =>
        console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
      );
    if (networkError) console.log(`[Network error]: ${networkError}`);
  })
});

const authLink = setContext(async (_, { headers }) => {
  return {
    headers: {
      ...headers,
      authorization: access_token ? `Bearer ${access_token}` : '',
      'Accept-Language': i18next?.language
    }
  };
});

const omitTypenameLink = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = omitDeep(operation.variables, ['__typename']);
  }
  return forward(operation);
});

const link = () =>
  split(
    // split based on operation type
    ({ query }) => {
      const { kind, operation } = getMainDefinition(query);
      return kind === 'OperationDefinition' && operation === 'subscription';
    },
    getWsLink(),
    httpLink
  );

const retryLink = new RetryLink({
  delay: {
    initial: 200,
    max: 2000,
    jitter: true
  },
  attempts: {
    max: 3
  }
});

const myAppLink = () => ApolloLink.from([networkStatusLink, omitTypenameLink, retryLink, authLink.concat(link())]);

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        documentAllowedTransitions: {
          merge: false
        }
      }
    },
    Partner: {
      keyFields: ['partnerId']
    },
    Asset: {
      keyFields: ['assetId'],
      fields: {
        equipment: { merge: false }
      }
    },
    AssetHierarchyLevel: {
      keyFields: ['id', 'label', 'level']
    },
    InsuranceData: {
      keyFields: ['documentId'],
      fields: {
        selectedPeriods: { merge: false },
        selectedInsurers: { merge: false }
      }
    },
    PolicyData: {
      keyFields: ['documentId']
    },
    Attachment: {
      keyFields: ['documentAttachmentId']
    },
    AttachmentData: {
      keyFields: ['documentId'],
      fields: {
        attachments: { merge: false }
      }
    },
    DocumentTransition: {
      keyFields: ['transitionId']
    },
    DriverData: {
      keyFields: ['documentId'],
      drivers: {
        merge: false
      }
    },
    CascoQuotationItem: {
      keyFields: ['quotationId'],
      fields: {
        risks: { merge: false },
        coefficients: { merge: false },
        totalDamageDeductibleCosts: { merge: false },
        partialDamageDeductibleCosts: { merge: false }
      }
    },
    QuotationRisk: {
      keyFields: ['quotationRiskId']
    },
    QuotationDeductibleCost: {
      keyFields: ['id']
    },
    Quotation: {
      keyFields: ['offerId'],
      fields: {
        details: { merge: false }
      }
    },
    QuotationDetail: { keyFields: ['offerDetailId'] },
    QuotationData: {
      keyFields: ['documentId'],
      fields: {
        quotations: { merge: false }
      }
    },
    Indicator: {
      keyFields: ['offerTypeId', 'insuranceTypeId', 'userId']
    },
    GraphicEvolution: {
      keyFields: ['offerTypeId', 'insuranceTypeId', 'year', 'month']
    },
    Comment: {
      keyFields: ['documentCommentId']
    },
    CommentData: {
      keyFields: ['documentId'],
      fields: {
        comments: { merge: false }
      }
    },
    DocumentHeader: {
      keyFields: ['documentId']
    },
    StateReason: {
      keyFields: ['documentStateXReasonId']
    },
    InsuranceDocumentMessage: {
      keyFields: ['documentId']
    },
    User: {
      keyFields: ['userId']
    },
    EngineType: {
      keyFields: ['name']
    },
    DocumentAllowedTransition: {
      keyFields: ['id', 'documentTypeId']
    },
    DocumentAllowedTransitions: {
      keyFields: ['documentStateId', 'documentTypeId']
    },
    DocumentStateUpdatedEvent: {
      keyFields: ['documentId', 'documentStateId']
    },
    HasAuthorizationType: {
      keyFields: ['code', 'documentStateId', 'documentTypeId']
    }
  }
});

cache.restore(defaultApolloCache);

export function setAccessToken(accessToken) {
  access_token = accessToken;
}

let apolloClient;
export const getApolloClient = () => {
  if (!apolloClient) {
    apolloClient = new ApolloClient({
      link: myAppLink(),
      cache
    });
  }
  return apolloClient;
};
